/**
 * RankModeBand — the strip across the top of a cycle's pages that says which
 * mode the cycle is in.
 *
 *   ranking  outlined: "Being prioritized. Ranks are not official yet."
 *   ranked   solid:    "In execution under <plan>, authorized <date> by <who>."
 *
 * Outlined and solid mean the same thing here as on a RankMark, so the band
 * and the numbers under it agree at a glance. Renders nothing while the mode
 * is loading or could not be read — a band that guesses is worse than none.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/i18n/formatters';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { fetchCycleRankMode, fetchPlanCycleRankMode, type RankMode } from '@/lib/rankMode';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type RankModeBandProps =
  /** A prioritization cycle's pages. */
  | { cycleId: string; planCycleId?: undefined }
  /** A plan cycle's pages. */
  | { planCycleId: string; cycleId?: undefined };

export function RankModeBand(props: RankModeBandProps) {
  const { cycleId, planCycleId } = props;
  const [mode, setMode] = useState<RankMode | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMode(null);
    const read = planCycleId ? fetchPlanCycleRankMode(planCycleId) : cycleId ? fetchCycleRankMode(cycleId) : null;
    read?.then((m) => {
      if (!cancelled) setMode(m);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [cycleId, planCycleId]);

  const by = mode?.mode === 'ranked' ? mode.authorizedBy : null;
  const actorIds = useMemo(() => (by && UUID_RE.test(by) ? [by] : []), [by]);
  const actorMap = useActorDisplayMap(actorIds);

  if (!mode) return null;

  if (mode.mode === 'ranking') {
    return (
      <div
        role="status"
        data-rank-mode="ranking"
        className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border border-dashed border-[var(--rule-bright)] px-4 py-2 text-sm"
      >
        <span className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-sec)]">
          {planCycleId ? 'Being planned' : 'Being prioritized'}
        </span>
        <span className="text-[var(--text-sec)]">
          Ranks are not of record yet. They go on record when a Plan of Record is authorized.
        </span>
      </div>
    );
  }

  const who = by ? (UUID_RE.test(by) ? actorMap[by] ?? null : by) : null;
  const when = formatDate(mode.authorizedAt, { month: 'short', day: 'numeric', year: 'numeric' });
  // On the plan's own pages the plan is this page; elsewhere, name it and link to it.
  const onThePlan = planCycleId === mode.planCycleId;

  return (
    <div
      role="status"
      data-rank-mode="ranked"
      className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 bg-[var(--text-pri)] px-4 py-2 text-sm text-[var(--bg)]"
    >
      <span className="font-mono text-xs uppercase tracking-[0.12em]">In execution</span>
      <span>
        {onThePlan ? 'Ranks are of record' : (
          <>
            Ranks are of record under{' '}
            <Link to={`/planning/plans/${mode.planCycleId}`} className="underline underline-offset-2">
              {mode.planName}
            </Link>
          </>
        )}
        {when ? `, authorized ${when}` : ''}
        {who ? ` by ${who}` : ''}. A rank changes only when a new Plan of Record is authorized.
      </span>
    </div>
  );
}
